/**
 * What a ladder member's load CELL says — the design handoff's "~119 kg / 85 % topu", and the
 * honesty rule behind it.
 *
 * A back-off has no authored weight. Its load is the top set's ACTUAL logged weight times its
 * percent, resolved during the client's session, so the builder can only ever show an ESTIMATE —
 * and the handoff is explicit that this is "celowa uczciwość": the tilde and the quiet italic are
 * the design saying "this number does not exist yet".
 *
 * ── What this module will NOT do, and why that is the whole point ─────────────────────────────
 * The estimate is derived from ONE source only: **the top set's own authored kilograms.** If the
 * top is prescribed as a percent of 1RM rather than in kg, there is no number here to multiply —
 * resolving it needs the client's estimated 1RM, which is gap G2 and arrives with PR-D. In that
 * case this returns the percent alone rather than a figure, because the alternative is inventing
 * one, and a fabricated future weight is exactly what the canon forbids.
 *
 * Same for a block with no top at all: a percent of nothing is nothing, and the table names that
 * shape inline instead.
 *
 * The estimate is **display only and is never written back**. Persisting it would turn a descent
 * that follows the client's real performance into a fixed weight — silently, and for good.
 *
 * ── This is not the progression matrix ───────────────────────────────────────────────────────
 * PR-D owns projecting a rule across a phase's cycles. This answers one narrower question about the
 * BASE cycle's own rows, needs no engine and no fixture, and is the only ladder arithmetic in
 * PR-B2C. The two must not be conflated: when the matrix lands, it resolves the same cell through
 * the fixture-pinned engine and this module keeps answering only for the authored base.
 *
 * No React, no DOM, no query hook, no i18next: this is `model/`.
 */

import type { SetDraft } from './planDraft.types';
import { derivesLoadInSession, resolveTopIndex } from './setShape';

/**
 * What a set's load cell shows.
 *
 * · `absolute`  — an authored weight in kg. The ordinary case.
 * · `percentOfOneRm` — an authored fraction of the client's 1RM, on a percent-mode exercise.
 * · `percentOfTop`   — a ladder member. `estimateKg` is the derived figure when the top's own load
 *                      is authored in kilograms, and `null` when it is not knowable yet.
 * · `empty`     — the exercise measures load and this set prescribes none. The client picks it.
 * · `none`      — the exercise does not measure load at all. No cell, not an empty one.
 */
export type SetLoadDisplay =
  | { readonly kind: 'absolute'; readonly kg: number }
  | { readonly kind: 'percentOfOneRm'; readonly fraction: number }
  | { readonly kind: 'percentOfTop'; readonly fraction: number; readonly estimateKg: number | null }
  | { readonly kind: 'empty' }
  | { readonly kind: 'none' };

/**
 * The top set's authored KILOGRAMS, or `null` when there is no top or the top does not carry one.
 *
 * Deliberately blind to `prescribedPercent`: a top prescribed at 87.5% of 1RM has no kilograms
 * until the client's 1RM is known, and treating the fraction as a weight would produce "~0,85 kg".
 */
export function topSetKilograms(sets: readonly SetDraft[]): number | null {
  const index = resolveTopIndex(sets);
  if (index === -1) return null;
  return sets[index]?.prescribedWeight ?? null;
}

/**
 * What one set's load cell says, given its exercise's contract and mode.
 *
 * `measuresWeight` is passed rather than read off the exercise, so this stays a function of the one
 * question it answers and can be exercised without building a whole `ExerciseDraft`.
 */
export function setLoadDisplay(
  set: SetDraft,
  sets: readonly SetDraft[],
  options: { readonly measuresWeight: boolean; readonly isPercentMode: boolean },
): SetLoadDisplay {
  if (!options.measuresWeight) return { kind: 'none' };

  if (derivesLoadInSession(set)) {
    const fraction = set.prescribedPercent;
    // A ladder member with no percent is a shape the backend refuses; the table shows an empty cell
    // for it rather than a "0 % of the top", which would read as a real prescription.
    if (fraction == null) return { kind: 'empty' };
    const topKg = topSetKilograms(sets);
    return {
      kind: 'percentOfTop',
      fraction,
      // Rounded to a tenth of a kilogram: the tilde already says the figure is approximate, and
      // "~119,00000000000001 kg" is what float multiplication gives without it.
      estimateKg: topKg === null ? null : Math.round(topKg * fraction * 10) / 10,
    };
  }

  if (options.isPercentMode) {
    const fraction = set.prescribedPercent;
    return fraction == null ? { kind: 'empty' } : { kind: 'percentOfOneRm', fraction };
  }

  const kg = set.prescribedWeight;
  return kg == null ? { kind: 'empty' } : { kind: 'absolute', kg };
}
